import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { matchPath, useNavigationType } from 'react-router';
import { getLocalStorage, setLocalStorage, guardedSessionStorage } from 'utils/fnStorage';
import { useWrapLocation } from 'hooks/useWrapRouter/useWrapLocation';
import { detailRouteMatch } from 'hooks/useDetailLocationParams';
import { StaticPageRoutes, CollectionRoute } from 'types/RouteTypes';
import { useResizeWatcher } from 'hooks/useResizeWatcher/useResizeWatcher';
import { useNavigation } from 'hooks/useNavigation';

const visitedLocations = [];
const DEBUG_HISTORY = false;

type InAppHistory = {
    key: string;
    path: string;
    search: string;
    isDetailPage: boolean;
};

const useAppService = () => {
    const appLanguageStorage = getLocalStorage('appLanguage');

    const [appLanguage, setAppLanguage] = useState<string>(appLanguageStorage || 'et');
    const [pageTitle, setPageTitle] = useState<string>(null);
    const [isCollectionPage, setIsCollectionPage] = useState<boolean>(false);
    const [appHistory, setAppHistory] = useState<InAppHistory[]>([]);
    const [noInternet, setNoInternet] = useState<boolean>(false);
    const [networkError, setNetworkError] = useState<number>(null);

    const popDetailHistory = useRef(false);

    const { isDesktop, isMobile, isMobileLandscape, isTablet, resolution } = useResizeWatcher();

    const changeApplicationLanguage = (toSetLanguage: string) => {
        setAppLanguage(toSetLanguage);
    };

    const popDetailHistoryHandler = () => {
        const historyLength = appHistory.length;

        if (historyLength) {
            let depth = 0;
            for (let i = historyLength - 1; i > 0; i -= 1) {
                if (appHistory[i].isDetailPage) {
                    depth += 1;
                } else {
                    break;
                }
            }

            if (depth > 0) {
                setAppHistory([...appHistory].slice(0, historyLength - depth));
            }
        }
    };

    /**
     * This is a HACK. It's necessary because navigation back from more detail Pages with the X so like history.go(-X)
     * trigger only once the in app history manager so can keep leaks in the memory. This function set's a flag so when the in-app history will change
     * this additionally pops the invalid items from the in app history.
     *
     */
    const popDetailLocations = () => {
        popDetailHistory.current = true;
    };

    const setGlobalNoInternet = () => {
        setNoInternet(true);
    };

    const setGlobalNetworkError = () => {
        setNetworkError(networkError);
    };

    useEffect(() => {
        setLocalStorage('appLanguage', appLanguage);
    }, [appLanguage]);

    useEffect(() => {
        if (popDetailHistory.current) {
            popDetailHistory.current = false;
            popDetailHistoryHandler();
        }
    }, [appHistory]);

    return {
        pageTitle,
        setPageTitle,
        appLanguage,

        isCollectionPage,
        setIsCollectionPage,
        setApplicationLanguage: changeApplicationLanguage,

        appHistory,
        setAppHistory,
        historyLength: appHistory.length,
        popDetailLocations,

        noInternet,
        setNoInternet,
        globalNoInternet: setGlobalNoInternet,

        networkError,
        setNetworkError,
        setGlobalNetworkError,

        isMobile,
        isMobileLandscape,
        isTablet,
        isDesktop,
        resolution,
    };
};

export const AppContext = createContext<ReturnType<typeof useAppService>>(null);

export const AppProvider = ({ children }) => {
    const appData = useAppService();

    return <AppContext.Provider value={appData}>{children}</AppContext.Provider>;
};

export const useApp = () => useContext(AppContext);

export const usePageUtils = () => {
    const { isCollectionPage: appIsCollectionPage } = useApp();
    const { location } = useNavigation();

    const isSettings = [StaticPageRoutes.SETTINGS, StaticPageRoutes.ABOUT].find((route) => location.pathname.indexOf(route) !== -1) != null;

    const isEpg = [StaticPageRoutes.EPG, StaticPageRoutes.EPG_ET, StaticPageRoutes.EPG_RU].some(
        (route) => location.pathname.indexOf(route) !== -1
    );

    const isRecordingsPage = [StaticPageRoutes.RECORDINGS, StaticPageRoutes.RECORDINGS_ET, StaticPageRoutes.RECORDINGS_RU].some(
        (route) => location.pathname.endsWith(route) || location.pathname.endsWith(`${route}/`)
    );

    const { appLanguage } = useApp();

    const isLiveNowPage = [CollectionRoute[appLanguage]?.NOW].some(
        (route) => location.pathname.endsWith(route) || location.pathname.endsWith(`${route}/`)
    );

    const isVodPurchasesPage = [StaticPageRoutes.VOD_PURCHASES, StaticPageRoutes.VOD_PURCHASES_ET, StaticPageRoutes.VOD_PURCHASES_RU].some(
        (route) => location.pathname.endsWith(route) || location.pathname.endsWith(`${route}/`)
    );

    const isContinueWatchingPage = [CollectionRoute[appLanguage]?.CONTINUE_WATCHING].some(
        (route) => route && (location.pathname.endsWith(route) || location.pathname.endsWith(`${route}/`))
    );

    const isCollectionPage = appIsCollectionPage || isRecordingsPage || isLiveNowPage || isVodPurchasesPage || isContinueWatchingPage;

    const isExpandablePage =
        matchPath(StaticPageRoutes.EXPANDABLE_PAGE, location.pathname) != null &&
        !isCollectionPage &&
        !isSettings &&
        !isEpg &&
        detailRouteMatch(location.pathname) == null;

    const pageBelongsToMainSections = !isExpandablePage;

    const isSearch = location.pathname === StaticPageRoutes.SEARCH;

    const hasBackNavigation = (!pageBelongsToMainSections || isCollectionPage || isSettings) && !isSearch;

    const hasSubpageBackNavigation = hasBackNavigation && !isCollectionPage;

    return {
        isSettings,
        pageBelongsToMainSections,
        isCollectionPage,
        isSearch,
        isEpg,
        isRecordingsPage,
        isVodPurchasesPage,
        isContinueWatchingPage,
        hasBackNavigation,
        hasSubpageBackNavigation,
    };
};

export const useAppHistory = () => {
    const navigationType = useNavigationType();
    const location = useWrapLocation();
    const { appHistory, setAppHistory, historyLength } = useApp();

    const path = location.pathname;
    const { key, search } = location;
    const isDetailPage = detailRouteMatch(path) != null;

    const handlePush = () => {
        if (!visitedLocations.includes(key)) {
            visitedLocations.push(key);
        }

        // Store return path when first entering a player page
        if (path.startsWith('/play/')) {
            const lastEntry = appHistory[appHistory.length - 1];
            if (lastEntry && !lastEntry.path.startsWith('/play/')) {
                guardedSessionStorage.setItem('channelSwitchReturnPath', lastEntry.path + lastEntry.search);
            }
        }

        setAppHistory([
            ...appHistory,
            {
                key,
                path,
                search,
                isDetailPage,
            },
        ]);

        if (DEBUG_HISTORY) {
            console.log('pushing...');
        }
    };

    const handlePop = () => {
        const poppedToIndex = appHistory.findIndex((item) => item.key === key);

        if (!historyLength) {
            // initial load
            handlePush();
        } else if (visitedLocations.includes(key) && poppedToIndex === -1) {
            handlePush();
        } else {
            setAppHistory([...appHistory].slice(0, poppedToIndex === -1 ? historyLength - 1 : poppedToIndex + 1));

            if (DEBUG_HISTORY) {
                console.log('popping...');
            }
        }
    };

    const handleReplace = () => {
        setAppHistory([...appHistory.slice(0, historyLength - 1), { key, path, search, isDetailPage }]);
        if (DEBUG_HISTORY) {
            console.log('replacing...');
        }
    };

    useEffect(() => {
        if (DEBUG_HISTORY) {
            console.log(appHistory);
        }
    }, [appHistory]);

    useEffect(() => {
        switch (navigationType) {
            case 'PUSH': {
                handlePush();
                break;
            }
            case 'POP': {
                handlePop();
                break;
            }
            case 'REPLACE': {
                handleReplace();
                break;
            }
            default: {
                console.log(`not supported action ${navigationType}`);
            }
        }
    }, [location.key]);
};
